import { Injectable, computed, signal } from '@angular/core';

import { GeoJsonPolygon } from '@core/utils/geo-polygon.utils';

/** Identity for a zone being created, which has no uid until it is first saved. */
export const NEW_AREA_UID = '__new_geographic_area__';

/** What the on-map hints describe: placing the first ring, or reshaping a closed one. */
export type GeoAreaEditMode = 'drawing' | 'adjusting';

/** An edit the map refused, told to the reader who attempted it. */
export type GeoAreaEditNotice = 'min-vertices';

export interface GeoAreaEditRequest {
  uid: string;
  geometry: GeoJsonPolygon | null;
  /** Name of the zone under edit, shown in the panel. Absent for a zone being created. */
  label?: string;
  /**
   * Whether the session may be committed with no boundary at all. True for a form field,
   * which can hold an empty optional geometry; false where the commit writes a row that
   * must keep a boundary, and clearing is only a step towards drawing another.
   */
  allowEmpty?: boolean;
  /** Run with the geometry the user settled on. Absent when the opener reads it itself. */
  onCommit?: (geometry: GeoJsonPolygon | null) => void;
  /** Run with the geometry the session opened with, so the opener can revert. */
  onCancel?: (geometry: GeoJsonPolygon | null) => void;
}

/**
 * The map renders areas from the visualization store, but the store is fed from the form,
 * which is fed from the map while a session runs. Refusing inbound data for the area under
 * edit breaks that cycle, so a drag is never overwritten mid-gesture.
 */
export function shouldApplyIncoming(areaUid: string, activeUid: string | null): boolean {
  return areaUid !== activeUid;
}

@Injectable({ providedIn: 'root' })
export class GeoAreaEditSessionService {
  private readonly activeUidState = signal<string | null>(null);
  private readonly geometryState = signal<GeoJsonPolygon | null>(null);
  private readonly labelState = signal<string | null>(null);
  private readonly noticeState = signal<GeoAreaEditNotice | null>(null);
  private readonly allowsEmptyState = signal(true);
  private readonly pendingVerticesState = signal(0);
  private initialGeometry: GeoJsonPolygon | null = null;
  private request: GeoAreaEditRequest | null = null;
  private finalizeDrawing: (() => void) | null = null;

  readonly activeUid = this.activeUidState.asReadonly();
  readonly geometry = this.geometryState.asReadonly();
  readonly label = this.labelState.asReadonly();
  readonly notice = this.noticeState.asReadonly();
  readonly allowsEmpty = this.allowsEmptyState.asReadonly();
  /** Vertices placed in a ring the map is still collecting, before it has been closed. */
  readonly pendingVertices = this.pendingVerticesState.asReadonly();
  readonly isActive = computed(() => this.activeUidState() !== null);

  /**
   * Draw commits a ring only once it closes, so a null geometry is exactly the state in
   * which the user is still placing points.
   */
  readonly mode = computed<GeoAreaEditMode>(() =>
    this.geometryState() === null ? 'drawing' : 'adjusting'
  );

  begin(request: GeoAreaEditRequest): void {
    this.request = request;
    this.initialGeometry = request.geometry;
    this.activeUidState.set(request.uid);
    this.geometryState.set(request.geometry);
    this.labelState.set(request.label ?? null);
    this.allowsEmptyState.set(request.allowEmpty ?? true);
    this.pendingVerticesState.set(0);
    this.noticeState.set(null);
  }

  /**
   * Registered by the map editor while it is attached. Committing runs it first so an open
   * ring is closed by Done itself: pressing enter beforehand is a shortcut, not a step.
   */
  setDrawingFinalizer(finalize: (() => void) | null): void {
    this.finalizeDrawing = finalize;
  }

  setPendingVertices(count: number): void {
    this.pendingVerticesState.set(count);
  }

  setGeometry(geometry: GeoJsonPolygon | null): void {
    this.geometryState.set(geometry);
    // A notice describes one attempted edit against one geometry; the next edit answers it.
    this.noticeState.set(null);
  }

  notify(notice: GeoAreaEditNotice): void {
    this.noticeState.set(notice);
  }

  clear(): void {
    this.setGeometry(null);
  }

  commit(): void {
    // Closing the ring feeds the drawing back through setGeometry synchronously, so the
    // geometry is read after, never before.
    this.finalizeDrawing?.();
    const onCommit = this.request?.onCommit;
    const geometry = this.geometryState();
    this.end();
    onCommit?.(geometry);
  }

  cancel(): void {
    const onCancel = this.request?.onCancel;
    const initial = this.initialGeometry;
    this.end();
    onCancel?.(initial);
  }

  end(): void {
    this.request = null;
    this.initialGeometry = null;
    this.activeUidState.set(null);
    this.geometryState.set(null);
    this.labelState.set(null);
    this.allowsEmptyState.set(true);
    this.pendingVerticesState.set(0);
    this.noticeState.set(null);
  }
}
